import { Paper } from "@mui/material";
import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_SECONDARY, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT } from "helpers/FONT";
import { SPACING, SPACING_DENSE, SPACING_HALF } from "helpers/spacings";
import React, { useMemo, useState } from "react";

const COMPARISON_STATS = [
  { key: "PTS", segTitle: "Points", colIndex: 0 },
  { key: "REB", segTitle: "Rebounds", colIndex: 0 },
  { key: "AST", segTitle: "Assists", colIndex: 0 },
  { key: "STL", segTitle: "Steals", colIndex: 0 },
  { key: "BLK", segTitle: "Blocks", colIndex: 0 },
];

function aggregateByTeam(segments) {
  const segMap = {};
  for (const seg of segments) segMap[seg.title] = seg;

  // Keyed by team id (rather than display name) so teams with identical/anonymised names don't collide.
  const byTeam = {};
  let userTeamId = null;

  for (const { key, segTitle, colIndex } of COMPARISON_STATS) {
    const seg = segMap[segTitle];
    if (!seg) continue;
    for (const row of seg.rows) {
      const teamId = row.teamId;
      if (!teamId) continue;
      const teamName = row.headCells.length > 1 ? row.headCells[1] : row.headCells[0];
      if (!byTeam[teamId]) byTeam[teamId] = { name: teamName, stats: {} };
      const val = parseFloat(row.cells[colIndex]?.value) || 0;
      byTeam[teamId].stats[key] = (byTeam[teamId].stats[key] || 0) + val;
      if (row.highlighted && !userTeamId) userTeamId = teamId;
    }
  }

  return { byTeam, teamIds: Object.keys(byTeam), userTeamId };
}

function formatVal(v) {
  if (!v) return "0";
  const rounded = Math.round(v * 10) / 10;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
}

const selectStyle = {
  width: "100%",
  padding: `${SPACING_DENSE} ${SPACING_HALF}`,
  fontFamily: FONT_FAMILY_GRAPHIK,
  fontSize: 13,
  border: `1px solid ${COLOR_GREY_BORDER}`,
  borderRadius: 8,
  backgroundColor: COLOR_WHITE_LIGHTER,
  color: COLOR_BLACK,
  cursor: "pointer",
  outline: "none",
};

export default function TeamsComparison({ segments = [], selectedTeamId = null }) {
  const { byTeam, teamIds, userTeamId } = useMemo(() => aggregateByTeam(segments), [segments]);

  const [selectedAId, selectedAIdSet] = useState(null);
  const [selectedBId, selectedBIdSet] = useState(null);

  const defaultTeamAId = selectedTeamId || userTeamId || teamIds[0] || "";
  const teamAId = (selectedAId && teamIds.includes(selectedAId) ? selectedAId : defaultTeamAId) || "";

  const defaultTeamBId = teamIds.find((id) => id !== teamAId) || "";
  const teamBId =
    (selectedBId && selectedBId !== teamAId && teamIds.includes(selectedBId) ? selectedBId : defaultTeamBId) || "";

  const teamAOptions = teamIds.filter((id) => id !== teamBId);
  const teamBOptions = teamIds.filter((id) => id !== teamAId);

  const teamA = byTeam[teamAId]?.name || "";
  const teamB = byTeam[teamBId]?.name || "";
  const statsA = byTeam[teamAId]?.stats || {};
  const statsB = byTeam[teamBId]?.stats || {};

  if (!teamIds.length) return null;

  return (
    <Paper style={{ borderRadius: 12, padding: SPACING }}>
      {/* Card title */}
      <div
        style={{
          fontFamily: FONT_FAMILY_KNOCKOUT,
          fontWeight: 400,
          fontSize: 15,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: COLOR_BLACK,
          marginBottom: SPACING,
          paddingBottom: SPACING_HALF,
          borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.08)}`,
        }}
      >
        Team Comparison
      </div>
      <div
        style={{
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontSize: 11,
          color: alpha(COLOR_BLACK, 0.45),
          marginTop: 4,
          marginBottom: SPACING,
        }}
      >
        Compare aggregate per-game averages against another team
      </div>

      {/* Team selectors */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: SPACING_HALF,
          marginBottom: SPACING,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: FONT_FAMILY_GRAPHIK,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: alpha(COLOR_BLACK, 0.75),
              marginBottom: 6,
            }}
          >
            Team
          </div>
          <select value={teamAId} onChange={(e) => selectedAIdSet(e.target.value)} style={selectStyle}>
            {teamAOptions.map((id) => (
              <option key={id} value={id}>
                {byTeam[id].name}
              </option>
            ))}
          </select>
        </div>

        {/* Swap icon */}
        <div
          style={{
            paddingBottom: 10,
            color: alpha(COLOR_BLACK, 0.35),
            fontSize: 18,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ⇌
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: FONT_FAMILY_GRAPHIK,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: alpha(COLOR_BLACK, 0.75),
              marginBottom: 6,
            }}
          >
            Compare To
          </div>
          <select value={teamBId} onChange={(e) => selectedBIdSet(e.target.value)} style={selectStyle}>
            {teamBOptions.map((id) => (
              <option key={id} value={id}>
                {byTeam[id].name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comparison bars */}
      <div style={{ display: "flex", flexFlow: "column nowrap", gap: 2 }}>
        {COMPARISON_STATS.map(({ key }) => {
          const aVal = statsA[key] || 0;
          const bVal = statsB[key] || 0;
          const max = Math.max(aVal, bVal) || 1;
          const aPct = (aVal / max) * 100;
          const bPct = (bVal / max) * 100;

          return (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: 44,
                fontFamily: FONT_FAMILY_GRAPHIK,
              }}
            >
              {/* Team A value */}
              <div
                style={{
                  width: 52,
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 13,
                  flexShrink: 0,
                  color: COLOR_BLACK,
                }}
              >
                {formatVal(aVal)}
              </div>

              {/* Team A bar (right-aligned, extends toward center) */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  paddingRight: 6,
                }}
              >
                <div
                  style={{
                    width: `${aPct}%`,
                    height: 20,
                    borderRadius: 6,
                    backgroundColor: COLOR_SECONDARY,
                    transition: "width 0.3s ease",
                    minWidth: aVal > 0 ? 4 : 0,
                  }}
                />
              </div>

              {/* Stat label */}
              <div
                style={{
                  width: 44,
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 0.5,
                  color: alpha(COLOR_BLACK, 0.75),
                  flexShrink: 0,
                }}
              >
                {key}
              </div>

              {/* Team B bar (left-aligned, extends away from center) */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                  paddingLeft: 6,
                }}
              >
                <div
                  style={{
                    width: `${bPct}%`,
                    height: 20,
                    borderRadius: 6,
                    backgroundColor: alpha(COLOR_BLACK, 0.15),
                    transition: "width 0.3s ease",
                    minWidth: bVal > 0 ? 4 : 0,
                  }}
                />
              </div>

              {/* Team B value */}
              <div
                style={{
                  width: 52,
                  textAlign: "right",
                  fontWeight: 600,
                  fontSize: 13,
                  flexShrink: 0,
                  color: alpha(COLOR_BLACK, 0.6),
                }}
              >
                {formatVal(bVal)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: SPACING_HALF,
          paddingTop: SPACING_DENSE,
          borderTop: `1px solid ${COLOR_GREY_BORDER}`,
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontSize: 11,
          color: alpha(COLOR_BLACK, 0.8),
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: COLOR_SECONDARY,
              flexShrink: 0,
            }}
          />
          {teamA}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {teamB}
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: alpha(COLOR_BLACK, 0.4),
              flexShrink: 0,
            }}
          />
        </div>
      </div>
    </Paper>
  );
}
